/**
 * 光影重构侧栏历史：日/夜 2700-6500K 仅展示 baseId-0/1/2 分片；过滤 legacy 无后缀聚合行。
 * 单图模式（阳光/微夜等）保留 baseId 行。
 */

export const LIGHT_RECONSTRUCT_MULTI_SHARD_INDICES = [0, 1, 2] as const;
export type LightReconstructShardIndex = (typeof LIGHT_RECONSTRUCT_MULTI_SHARD_INDICES)[number];

const SHARD_SUFFIX = /^-([0-2])$/;

export function isLightReconstructDayNightModeLabel(raw?: string): boolean {
  const ml = String(raw ?? '').trim();
  if (!ml) return false;
  if (ml.includes('2700') && ml.includes('6500')) return true;
  if (/day.*2700.*6500/i.test(ml) || /night.*2700.*6500/i.test(ml)) return true;
  return false;
}

export function parseLightReconstructShardIndex(id: string): LightReconstructShardIndex | undefined {
  const m = String(id ?? '').trim().match(/-([0-2])$/);
  if (!m) return undefined;
  const n = Number(m[1]);
  if (n === 0 || n === 1 || n === 2) return n as LightReconstructShardIndex;
  return undefined;
}

/** 日/夜任务的无后缀 baseId（Job 旧聚合行） */
export function isAggregateLightReconstructDayNightHistoryId(id: string, modeLabel?: string): boolean {
  if (!isLightReconstructDayNightModeLabel(modeLabel)) return false;
  return parseLightReconstructShardIndex(id) === undefined;
}

export function lightReconstructHistoryBaseId(id: string): string {
  const s = id.trim();
  const m = s.match(/^(.+)-([0-2])$/);
  return m ? m[1] : s;
}

export function isLightReconstructDisplayHistoryItem(item: {
  id?: string;
  modeLabel?: string;
}): boolean {
  const id = String(item.id ?? '').trim();
  if (!id) return false;
  const dayNight = isLightReconstructDayNightModeLabel(item.modeLabel);
  if (dayNight) {
    if (isAggregateLightReconstructDayNightHistoryId(id, item.modeLabel)) return false;
    return parseLightReconstructShardIndex(id) !== undefined;
  }
  return true;
}

/**
 * 云端/本地历史进入 UI：日/夜按分片去重（每任务最多 3 条）；单图模式原样保留。
 */
export function filterLightReconstructHistoryForDisplay<
  T extends { id: string; modeLabel?: string; timestamp: number },
>(items: T[]): T[] {
  const singleItems: T[] = [];
  const byBase = new Map<string, Map<LightReconstructShardIndex, T>>();

  for (const raw of items) {
    if (!isLightReconstructDisplayHistoryItem(raw)) continue;
    if (!isLightReconstructDayNightModeLabel(raw.modeLabel)) {
      singleItems.push(raw);
      continue;
    }
    const shard = parseLightReconstructShardIndex(raw.id);
    if (shard === undefined) continue;
    const base = lightReconstructHistoryBaseId(raw.id);
    if (!byBase.has(base)) byBase.set(base, new Map());
    const bucket = byBase.get(base)!;
    const existing = bucket.get(shard);
    if (!existing || raw.timestamp > existing.timestamp) {
      bucket.set(shard, raw);
    }
  }

  const shardItems: T[] = [];
  for (const bucket of byBase.values()) {
    for (const idx of LIGHT_RECONSTRUCT_MULTI_SHARD_INDICES) {
      const it = bucket.get(idx);
      if (it) shardItems.push(it);
    }
  }

  return [...shardItems, ...singleItems].sort((a, b) => b.timestamp - a.timestamp);
}
